/**
 * Admin/SA list of the public landing-page hero banners (the carousel).
 * Reorder / show-hide / delete here; create + edit happen on the full-page
 * editor (/admin/banners/new, /admin/banners/:id). Gated by BANNER.MANAGE.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ImageOff, Megaphone } from 'lucide-react';
import { request } from '../../api/client';
import { Button, Switch, Modal, toast, cn } from '../../ui';

interface Banner {
  id: string;
  eyebrow?: string | null;
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  showOverlay: boolean;
  ctaLabel?: string | null;
  ctaLink?: string | null;
  secondaryCtaLabel?: string | null;
  secondaryCtaLink?: string | null;
  sortOrder: number;
  isActive: boolean;
}

export function BannersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState<Banner | null>(null);

  const { data: banners = [], isLoading } = useQuery({
    queryKey: ['admin-banners'],
    queryFn: () => request<Banner[]>({ url: '/banners' }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-banners'] });
    qc.invalidateQueries({ queryKey: ['public-banners'] });
  };

  const toggleMut = useMutation({
    mutationFn: (b: Banner) =>
      request({
        url: `/banners/${b.id}`,
        method: 'PUT',
        data: {
          eyebrow: b.eyebrow || undefined,
          title: b.title,
          subtitle: b.subtitle || undefined,
          imageUrl: b.imageUrl || undefined,
          showOverlay: b.showOverlay,
          ctaLabel: b.ctaLabel || undefined,
          ctaLink: b.ctaLink || undefined,
          secondaryCtaLabel: b.secondaryCtaLabel || undefined,
          secondaryCtaLink: b.secondaryCtaLink || undefined,
          isActive: !b.isActive,
        },
      }),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Could not update'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => request({ url: `/banners/${id}`, method: 'DELETE' }),
    onSuccess: () => { invalidate(); setConfirmDelete(null); toast.success('Banner deleted'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Could not delete banner'),
  });

  const reorderMut = useMutation({
    mutationFn: (ids: string[]) => request({ url: '/banners/reorder', method: 'POST', data: { ids } }),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Could not reorder'),
  });

  const move = (index: number, dir: -1 | 1) => {
    const next = [...banners];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    reorderMut.mutate(next.map((b) => b.id));
  };

  const activeCount = banners.filter((b) => b.isActive).length;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-xl font-semibold text-content">
            <Megaphone className="size-6 text-brand-600" /> Landing Banners
          </h1>
          <p className="mt-1 text-sm text-muted">
            Design a banner, upload it, and it shows full-width in the home-page carousel. Active banners rotate in order.
          </p>
        </div>
        <Button variant="primary" icon={<Plus />} onClick={() => navigate('/admin/banners/new')}>Add banner</Button>
      </div>

      <div className="mt-5 flex gap-3 text-sm">
        <span className="rounded-pill border border-line bg-surface px-3 py-1 text-muted">{banners.length} total</span>
        <span className="rounded-pill border border-brand-200 bg-brand-50 px-3 py-1 font-semibold text-brand-700 dark:border-brand-900/50 dark:bg-brand-900/25 dark:text-brand-300">
          {activeCount} active
        </span>
      </div>

      <div className="mt-6 space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-card border border-line bg-surface" />
          ))
        ) : banners.length === 0 ? (
          <div className="grid place-items-center rounded-card border border-dashed border-line bg-surface py-20 text-center">
            <Megaphone className="size-12 text-subtle" />
            <p className="mt-4 text-lg font-semibold text-content">No banners yet</p>
            <p className="mt-1 text-sm text-muted">Add your first banner to populate the home-page carousel.</p>
            <Button className="mt-5" variant="primary" icon={<Plus />} onClick={() => navigate('/admin/banners/new')}>Add banner</Button>
          </div>
        ) : (
          banners.map((b, i) => (
            <div
              key={b.id}
              className={cn(
                'flex items-center gap-4 rounded-card border bg-surface p-4 shadow-soft',
                b.isActive ? 'border-line' : 'border-dashed border-line opacity-70',
              )}
            >
              <div className="flex flex-col">
                <button type="button" aria-label="Move up" disabled={i === 0 || reorderMut.isPending} onClick={() => move(i, -1)}
                  className="grid size-7 place-items-center rounded-control text-muted hover:bg-surface-2 hover:text-content disabled:opacity-30">
                  <ArrowUp className="size-4" />
                </button>
                <button type="button" aria-label="Move down" disabled={i === banners.length - 1 || reorderMut.isPending} onClick={() => move(i, 1)}
                  className="grid size-7 place-items-center rounded-control text-muted hover:bg-surface-2 hover:text-content disabled:opacity-30">
                  <ArrowDown className="size-4" />
                </button>
              </div>

              <button type="button" onClick={() => navigate(`/admin/banners/${b.id}`)}
                className="grid h-16 w-28 shrink-0 place-items-center overflow-hidden rounded-lg bg-gradient-to-br from-brand-700 to-brand-600">
                {b.imageUrl ? (
                  <img src={b.imageUrl} alt="" className="size-full object-cover" />
                ) : (
                  <ImageOff className="size-6 text-white/70" />
                )}
              </button>

              <button type="button" onClick={() => navigate(`/admin/banners/${b.id}`)} className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    'rounded-pill px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wide',
                    b.imageUrl ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' : 'bg-surface-2 text-muted',
                  )}>
                    {b.imageUrl ? (b.showOverlay ? 'Image + text' : 'Image') : 'Text only'}
                  </span>
                </div>
                <div className="mt-1 truncate font-bold text-content">{b.title.replace(/\n/g, ' · ')}</div>
                {b.subtitle && <div className="truncate text-sm text-muted">{b.subtitle}</div>}
              </button>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={b.isActive} onCheckedChange={() => toggleMut.mutate(b)} />
                  <span className={cn('text-xs font-semibold', b.isActive ? 'text-brand-700 dark:text-brand-400' : 'text-subtle')}>
                    {b.isActive ? 'Active' : 'Hidden'}
                  </span>
                </div>
                <Button size="sm" variant="default" icon={<Pencil />} onClick={() => navigate(`/admin/banners/${b.id}`)}>Edit</Button>
                <Button size="sm" variant="text" icon={<Trash2 />} className="text-danger-500! hover:bg-danger-500/10!"
                  onClick={() => setConfirmDelete(b)} aria-label="Delete banner" />
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Delete banner?"
        width={420}
        footer={
          <>
            <Button variant="default" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="danger" loading={deleteMut.isPending} onClick={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}>Delete</Button>
          </>
        }
      >
        <p className="text-sm text-muted">
          “{confirmDelete?.title.replace(/\n/g, ' ')}” will be removed from the carousel. This can’t be undone.
        </p>
      </Modal>
    </div>
  );
}
